import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Reports > Shift Reliability: who turns up for their shifts and who does not, from the permanent shift record.
// Attended and no-shows are recorded when a planner day is locked; rejections when they happen.
const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const KIND_LABELS = { game: 'Game', coach: 'Coach', court: 'Court' };
const OUTCOME_LABELS = { Attended: 'Attended', NoShow: 'No-show', Rejected: 'Rejected' };
const OUTCOME_COLOURS = { Attended: '#2e7d32', NoShow: '#c62828', Rejected: '#e65100' };

const th = { textAlign: 'left', padding: '8px 10px', fontWeight: 700, color: '#333', background: '#fafafa', borderBottom: '2px solid #e0e0e0', fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' };
const td = { padding: '7px 10px', borderBottom: '1px solid #f0f0f0', fontSize: '0.85rem', verticalAlign: 'top' };

function csvCell(v) {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([rows.map((r) => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ShiftReliabilityReport({ minDate }) {
  const today = new Date();
  const ninetyAgo = new Date();
  ninetyAgo.setDate(ninetyAgo.getDate() - 90);
  const [start, setStart] = useState(isoDate(ninetyAgo));
  const [end, setEnd] = useState(isoDate(today));
  const [kind, setKind] = useState('');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ key: 'noShows', dir: 'desc' });
  const [openPerson, setOpenPerson] = useState(null);
  const [detail, setDetail] = useState({ personId: null, items: [], loading: false });
  const [showAttended, setShowAttended] = useState(false);

  const kindQuery = kind ? `&kind=${kind}` : '';

  const run = useCallback(async () => {
    setLoading(true);
    setError('');
    setOpenPerson(null);
    try {
      const res = await fetch(`${API_BASE}/reports/shift-reliability?start=${start}&end=${end}${kindQuery}`);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      setRows(await res.json());
    } catch (err) {
      setError(err.message);
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [start, end, kindQuery]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);

  async function toggleDetail(personId) {
    if (openPerson === personId) { setOpenPerson(null); return; }
    setOpenPerson(personId);
    setDetail({ personId, items: [], loading: true });
    try {
      const res = await fetch(`${API_BASE}/reports/shift-reliability/${personId}?start=${start}&end=${end}${kindQuery}`);
      setDetail({ personId, items: res.ok ? await res.json() : [], loading: false });
    } catch {
      setDetail({ personId, items: [], loading: false });
    }
  }

  const sorted = useMemo(() => {
    if (!rows) return [];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (sort.key === 'name') return String(av).localeCompare(String(bv)) * dir;
      // A person with no rate yet sorts last either way.
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir || String(a.name).localeCompare(String(b.name));
    });
  }, [rows, sort]);

  const totals = useMemo(() => (rows || []).reduce((t, r) => ({
    attended: t.attended + r.attended, noShows: t.noShows + r.noShows, rejected: t.rejected + r.rejected
  }), { attended: 0, noShows: 0, rejected: 0 }), [rows]);

  const sortTh = (key, label) => (
    <th style={th} onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))}>
      {label}{sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const exportCsv = () => {
    downloadCsv(`shift-reliability-${start}-to-${end}.csv`, [
      ['Person', 'Attended', 'No-shows', 'Rejected', 'Rejected for time off', 'Turn-up %'],
      ...sorted.map((r) => [r.name, r.attended, r.noShows, r.rejected, r.rejectedForTimeOff, r.turnUpRate == null ? '' : r.turnUpRate])
    ]);
  };

  const shownDetail = detail.items.filter((i) => showAttended || i.outcome !== 'Attended');

  return (
    <>
      <div className="report-controls">
        <div className="control-group">
          <label>From:</label>
          <input type="date" value={start} min={minDate} max={end} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="control-group">
          <label>To:</label>
          <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <div className="control-group">
          <label>Shifts:</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">All (games, coaching, court)</option>
            <option value="game">Games (referee and bench)</option>
            <option value="coach">Coaching</option>
            <option value="court">Court</option>
          </select>
        </div>
        <div className="control-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <button className="btn-generate" style={{ padding: '0.45rem 1.1rem', fontSize: '0.85rem' }} onClick={run} disabled={loading}>
            {loading ? 'Loading…' : 'Generate Report'}
          </button>
          {rows && rows.length > 0 && (
            <button className="btn-generate" style={{ padding: '0.45rem 1.1rem', fontSize: '0.85rem' }} onClick={exportCsv}>Export CSV</button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {rows && (
        <div style={{ marginTop: '1rem' }}>
          <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
            Turn-up % is attended out of attended plus no-shows. Attendance and no-shows are recorded when a day is locked in the planner; rejections are recorded the moment they happen (including shifts rejected because of time off) and are never deleted.
          </p>
          <p style={{ margin: '0 0 0.75rem', fontWeight: 600 }}>
            {totals.attended} attended · <span style={{ color: '#c62828' }}>{totals.noShows} no-shows</span> · <span style={{ color: '#e65100' }}>{totals.rejected} rejected</span>
          </p>
          {rows.length === 0 ? (
            <p style={{ color: '#888' }}>Nothing recorded for this period.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {sortTh('name', 'Person')}
                    {sortTh('attended', 'Attended')}
                    {sortTh('noShows', 'No-shows')}
                    {sortTh('rejected', 'Rejected')}
                    {sortTh('turnUpRate', 'Turn-up %')}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <React.Fragment key={r.personId}>
                      <tr onClick={() => toggleDetail(r.personId)} style={{ cursor: 'pointer', background: openPerson === r.personId ? '#fef0f3' : undefined }}>
                        <td style={{ ...td, fontWeight: 600 }}>{openPerson === r.personId ? '▾ ' : '▸ '}{r.name}</td>
                        <td style={td}>{r.attended}</td>
                        <td style={{ ...td, color: r.noShows ? '#c62828' : undefined, fontWeight: r.noShows ? 700 : 400 }}>{r.noShows}</td>
                        <td style={{ ...td, color: r.rejected ? '#e65100' : undefined }}>
                          {r.rejected}{r.rejectedForTimeOff ? ` (${r.rejectedForTimeOff} for time off)` : ''}
                        </td>
                        <td style={td}>{r.turnUpRate == null ? '–' : `${r.turnUpRate}%`}</td>
                      </tr>
                      {openPerson === r.personId && (
                        <tr>
                          <td colSpan={5} style={{ ...td, background: '#fafafa' }}>
                            <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginBottom: 8, fontSize: '0.85rem' }}>
                              <input type="checkbox" checked={showAttended} onChange={(e) => setShowAttended(e.target.checked)} /> Also show shifts they attended
                            </label>
                            {detail.loading ? <p>Loading…</p> : shownDetail.length === 0 ? (
                              <p style={{ color: '#888', margin: 0 }}>No rejected shifts or no-shows in this period.</p>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>{['Date', 'Shift', 'Role', 'Outcome', 'Note'].map((h) => <th key={h} style={{ ...th, cursor: 'default' }}>{h}</th>)}</tr>
                                </thead>
                                <tbody>
                                  {shownDetail.map((i) => (
                                    <tr key={i.id}>
                                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        {new Date(`${i.shiftDate}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                        {i.shiftStart && i.shiftStart.slice(11, 16) !== '00:00' ? ` ${i.shiftStart.slice(11, 16)}` : ''}
                                      </td>
                                      <td style={td}>{KIND_LABELS[i.kind]}: {i.title}</td>
                                      <td style={td}>{i.role || ''}</td>
                                      <td style={{ ...td, color: OUTCOME_COLOURS[i.outcome], fontWeight: 700 }}>{OUTCOME_LABELS[i.outcome]}</td>
                                      <td style={td}>{i.reason ? `${i.reason}${i.source ? ' · ' : ''}` : ''}{i.outcome === 'Rejected' && i.source ? `via ${i.source}` : ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}
